import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
} from "@mui/material";
import { useField } from "formik";

import { useGetUtilityLocales } from "hooks/useGetUtilityLocales";
import { LOCALE_DEFAULT } from "../../../constants/constants";

type LocaleSelectProps = SelectProps & {
  name: string;
};

export const LocaleSelect = ({
  name,
  ...props
}: LocaleSelectProps): JSX.Element => {
  const [{ value, onBlur }, { error, touched }, { setValue }] = useField(name);
  const { t } = useTranslation();
  const { locales, isLoading } = useGetUtilityLocales();
  const hasError = useMemo(() => touched && Boolean(error), [touched, error]);
  const onChange = useCallback(
    ({ target }) => {
      setValue(target.value);
    },
    [setValue]
  );

  return (
    <FormControl error={hasError} size={props.size} fullWidth={props.fullWidth}>
      <InputLabel>{props.label}</InputLabel>
      <Select
        {...props}
        label={props.label}
        value={value ?? LOCALE_DEFAULT}
        onBlur={onBlur}
        displayEmpty
        disabled={props.disabled || isLoading}
        onChange={onChange}
        renderValue={
          isLoading
            ? () => (
                <>
                  <CircularProgress size={16} sx={{ marginRight: 2 }} />
                  {t("Loading")}
                </>
              )
            : undefined
        }
      >
        {locales.map((locale) => (
          <MenuItem key={locale.value} value={locale.value}>
            {locale.name}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText error>{hasError ? error : " "}</FormHelperText>
    </FormControl>
  );
};
